import { axiosInstanceAtom, getResourceHeadersWithTokenAtom, Store } from '../../state';
import { NbLogger } from '../../utils';

export type SSEClientEvents<T> = {
  onConnectSuccess: (silentConnectRequested: boolean) => void;
  onConnectError: (silentConnectRequested: boolean) => void;
  onMessageCallback: (data: T) => void;
};

export interface EventSourceLike extends EventSource {
  readyState: number;
  onopen: ((this: EventSource, ev: Event) => any) | null;
  onerror: ((this: EventSource, ev: Event) => any) | null;
}

/**
 * Base class for creating SSE client
 */
export class SSEClient<T> {
  protected client: EventSourceLike | null = null;
  protected connectFailed = false;
  protected token = '';
  protected eventHandlers?: SSEClientEvents<T>;
  protected validMessageType = 'message';

  private silentConnectRequested = false;

  protected constructor(
    protected readonly clientConstructor: (token: string) => EventSourceLike,
    protected readonly store: Store,
    protected logger: NbLogger,
    protected sseEventLogger: ((message: any) => void) | null,
  ) {}

  setToken(token: string) {
    this.token = token;
  }

  protected get axiosInstance() {
    return this.store.get(axiosInstanceAtom)?.fn();
  }

  protected get getResourceHeadersWithToken() {
    return this.store.get(getResourceHeadersWithTokenAtom)?.fn();
  }

  setEventHandlers(eventHandlers: SSEClientEvents<T>) {
    this.eventHandlers = eventHandlers;
  }

  /**
   * Create a connection to the SSE server. Result of the connection should be handled in the set
   */
  connect(isSilent?: boolean): void {
    if (this.client) {
      return;
    }

    this.silentConnectRequested = !!isSilent;
    try {
      // when event source URL is not reachable the client will call onerror in another scope,
      // so we need to memoize this reject function, so we can reject connect
      this.connectFailed = false;

      this.client = this.clientConstructor(this.token);
      this.client.addEventListener(this.validMessageType, this.onMessageReceived.bind(this));
      this.client.onerror = this.onError.bind(this);

      // onopen called when connection created
      this.client.onopen = () => {
        this.connectFailed = false;
        this.onConnectSuccess(this.silentConnectRequested);
        this.eventHandlers?.onConnectSuccess(this.silentConnectRequested);
      };
    } catch (err) {
      this.logger.error(`Failed to create eStream client`, err);
      this.connectFailed = true;
      this.disconnect();
      this.eventHandlers?.onConnectError(this.silentConnectRequested);
    }
  }

  disconnect() {
    if (this.client) {
      this.client.close();
    }
    this.client?.removeEventListener(this.validMessageType, this.onMessageReceived.bind(this));
    this.client = null;
  }

  hasToken() {
    return !!this.token;
  }

  /* eslint-disable @typescript-eslint/no-unused-vars */
  protected handleMessage(message: T) {
    if (this.eventHandlers) {
      this.eventHandlers.onMessageCallback(message);
    }
  }

  /* eslint-disable @typescript-eslint/no-unused-vars */
  protected onConnectSuccess(silentConnectRequested: boolean) {}

  private onMessageReceived(ev: MessageEvent) {
    if (ev.type === this.validMessageType) {
      try {
        const message = JSON.parse(ev.data as string);
        if (this.validMessageType === 'prices') {
          // debugEStreamTrades(message);
          if (this.sseEventLogger) {
            this.sseEventLogger({ ...message, timestamp: new Date(message.timestamp) });
          }
        }

        // this.logger.trace(`Message received from eStream`, message);
        this.handleMessage(message as T);
      } catch (err) {
        this.logger.error(`Invalid message`, ev.data);
      }
    } else {
      this.logger.error(`Unknown message type`, ev);
    }
  }

  protected onError(ev: Event) {
    if (this.eventHandlers?.onConnectError) {
      console.error(`Error from eStream`, ev);
      this.logger.error(`Error from sseClient (readyState=${this.client?.readyState})`);
      this.disconnect();
      this.eventHandlers?.onConnectError(this.silentConnectRequested);
    }
  }
}
